import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { KnownLockup, KnownMark, KnownWordmark } from "./Brand";
import { BarChart3, Check, ChevronDown, LayoutGrid, MoreHorizontal, Plus, Settings, ShieldCheck, Sun, UserCog, UserRound, Users } from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import { ROLE_LABEL, can, roleLabel, roleText, useOrg } from "./org";

const navItems = (m) => {
  const c = can(m);
  return [
    { to: "/", label: c.seesCare ? "Today" : "Home", Icon: Sun, id: "today" },
    c.oversee && { to: "/overview", label: "Overview", Icon: BarChart3, id: "overview" },
    { to: "/people", label: c.leader ? "My People" : "People", Icon: Users, id: "people" },
    { to: "/groups", label: "Groups", Icon: LayoutGrid, id: "groups" },
    c.team && { to: "/team", label: "Team", Icon: UserCog, id: "team" },
    c.directory && { to: "/settings", label: "Settings", Icon: Settings, id: "settings" },
    c.audit && { to: "/audit", label: "Audit History", Icon: ShieldCheck, id: "audit" },
  ].filter(Boolean);
};

const ChurchSwitcher = ({ className }) => {
  const { me, membership, switchOrg } = useOrg();
  const navigate = useNavigate();
  return (
    <DropdownMenu>
      <DropdownMenuTrigger className={cn("flex items-center gap-1.5 min-w-0 min-h-[44px] text-left text-ink-soft hover:text-ink transition-colors", className)} data-testid="church-switcher">
        <span className="line-clamp-2 text-[15px] leading-snug" title={membership.org_name} data-testid="current-church-name">{membership.org_name}</span>
        <ChevronDown size={15} className="shrink-0" />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="min-w-[260px]">
        {me.memberships.map((m) => (
          <DropdownMenuItem key={m.org_id} onSelect={() => { switchOrg(m.org_id); navigate("/"); }} className="py-3" data-testid={`switch-church-${m.org_id}`}>
            <div className="flex-1 min-w-0"><p className="truncate font-medium">{m.org_name}</p><p className="text-xs text-ink-faint">{roleLabel(m, m.role)}</p></div>
            {m.org_id === membership.org_id && <Check size={16} />}
          </DropdownMenuItem>
        ))}
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={() => navigate("/new-church")} className="py-3" data-testid="switch-new-church"><Plus size={16} className="mr-2" /> Set up another church</DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

const SideLink = ({ it }) => (
  <NavLink to={it.to} end={it.to === "/"} className={({ isActive }) => cn("h-12 px-3 rounded-[12px] flex items-center gap-3 text-[16px] font-medium transition-colors", isActive ? "bg-sunk text-ink" : "text-ink-soft hover:bg-sunk/60 hover:text-ink")} data-testid={`nav-${it.id}`}>
    <it.Icon size={20} strokeWidth={1.8} />{it.label}
  </NavLink>
);

const Sidebar = () => {
  const { me, membership } = useOrg();
  return (
    <aside className="hidden lg:flex fixed inset-y-0 left-0 w-[264px] border-r border-line bg-paper flex-col px-5 py-7 z-20" data-testid="desktop-sidebar">
      <div className="px-3"><KnownLockup size="md" testid="sidebar-brand" /></div>
      <ChurchSwitcher className="px-3 mt-4" />
      <nav className="mt-10 flex flex-col gap-1">{navItems(membership).map((it) => <SideLink key={it.id} it={it} />)}</nav>
      <NavLink to="/me" className={({ isActive }) => cn("mt-auto flex items-center gap-3 px-2 pt-6 pb-1 border-t border-line rounded-b-[12px] transition-colors", isActive ? "text-ink" : "hover:text-ink")} data-testid="sidebar-user">
        <MeAvatar name={me.user.name} />
        <div className="min-w-0"><p className="text-ink font-medium leading-tight truncate">{me.user.name}</p><p className="text-sm text-ink-faint">{roleLabel(membership, membership.role)}{membership.confidential_access ? " · Confidential" : ""}</p></div>
      </NavLink>
    </aside>
  );
};

const MeAvatar = ({ name }) => {
  const [f, l] = name.split(" ");
  return <span className="h-9 w-9 rounded-full bg-sunk text-ink-soft font-serif text-[15px] inline-flex items-center justify-center shrink-0">{f?.[0]}{l?.[0]}</span>;
};

const MobileBar = () => (
  <header className="lg:hidden sticky top-0 z-20 bg-[color:var(--paper-glass)] backdrop-blur-xl border-b border-line px-5 h-14 flex items-center gap-3" data-testid="mobile-topbar">
    <div className="flex items-center gap-3 min-w-0"><span className="inline-flex items-center gap-2 shrink-0" data-testid="mobile-brand" aria-label="KNOWN Church Care"><KnownMark size={26} /><KnownWordmark size="sm" sub={false} /></span><span className="w-px h-5 bg-line" aria-hidden="true" /><ChurchSwitcher /></div>
  </header>
);

const BottomNav = () => {
  const { membership } = useOrg();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const items = [...navItems(membership), { to: "/me", label: "Me", Icon: UserRound, id: "me" }];
  const main = items.length <= 4 ? items : items.slice(0, 3);
  const more = items.length <= 4 ? [] : items.slice(3);
  const tab = ({ isActive }) => cn("flex-1 flex flex-col items-center gap-1 pt-2 text-[12px] font-medium transition-colors", isActive ? "text-ink" : "text-ink-faint");
  return (
    <nav className="lg:hidden fixed bottom-0 inset-x-0 z-30 bg-[color:var(--paper-glass)] backdrop-blur-xl border-t border-line pb-5 pt-1 flex items-start" data-testid="bottom-nav">
      {main.map((it) => <NavLink key={it.id} to={it.to} end={it.to === "/"} className={tab} data-testid={`bottom-nav-${it.id}`}><it.Icon size={24} strokeWidth={1.8} />{it.label}</NavLink>)}
      {more.length > 0 && (
        <DropdownMenu>
          <DropdownMenuTrigger className={cn("flex-1 flex flex-col items-center gap-1 pt-2 text-[12px] font-medium", more.some((it) => pathname.startsWith(it.to)) ? "text-ink" : "text-ink-faint")} data-testid="bottom-nav-more"><MoreHorizontal size={24} strokeWidth={1.8} />More</DropdownMenuTrigger>
          <DropdownMenuContent align="end" side="top" className="min-w-[200px]">
            {more.map((it) => <DropdownMenuItem key={it.id} onSelect={() => navigate(it.to)} className="py-3 gap-3" data-testid={`bottom-nav-${it.id}`}><it.Icon size={18} />{it.label}</DropdownMenuItem>)}
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </nav>
  );
};

export const Shell = ({ children }) => (
  <div className="min-h-screen bg-paper text-ink font-sans" data-testid="app-shell">
    <Sidebar />
    <MobileBar />
    <main className="lg:pl-[264px] pb-28 lg:pb-0">
      <div className="max-w-[1200px] mx-auto px-5 sm:px-8 lg:px-14 py-8 sm:py-12">{children}</div>
    </main>
    <BottomNav />
  </div>
);

export const NoAccess = () => {
  const { membership } = useOrg();
  return (
  <div data-testid="no-access-screen">
    <p className="t-label">Not available</p>
    <h1 className="t-greeting mt-3">This page isn't part of your role.</h1>
    <p className="t-lead mt-4">{roleText(membership, "If you think you should have access, ask your church's Owner.")}</p>
    <NavLink to="/" className="btn btn-primary mt-8" data-testid="no-access-home-btn">Back to Today</NavLink>
  </div>
  );
};
